<template>
  <div data-testid="instrument-details">
    <BaseModal :is-open="isOpen" size="lg" v-on="$listeners">
      <template #header>
        <NavBar>
          <template #center>
            <span>Detalhes do produto</span>
          </template>
          <template #right>
            <RoundedIcon
              data-testid="close-button"
              icon-name="ic_cancel"
              main-color="transparent"
              :hover-color="NebraskaColors.backgroundHover"
              :text-color="NebraskaColors.elementPrimary"
              :hover-text-color="NebraskaColors.elementPrimary"
              :has-hover="true"
              @click="$emit('close', instrument)"
            />
          </template>
        </NavBar>
      </template>
      <template #content>
        <LoaderIndicator v-if="isLoading" class="size-load" with-container />

        <div v-if="!isLoading && instrument">
          <div class="modal-content">
            <div data-testid="header-instrument">
              <Heading :level="2">{{ instrument.name }}</Heading>
              <p class="subtitle">{{ instrument.subCategory }}</p>
            </div>

            <div class="my-xm">
              <Heading :level="3">Características</Heading>

              <Row
                data-testid="profitability-row"
                :has-interaction="false"
                :has-line="true"
              >
                <template #top-left>
                  <span class="row-title">Rentabilidade (12 meses)</span>
                </template>
                <template #top-right>
                  <span>
                    {{ instrument.profitability }}
                  </span>
                </template>
              </Row>

              <Row
                data-testid="minimumInitialInvestment-row"
                :has-interaction="false"
                :has-line="true"
              >
                <template #top-left>
                  <span class="row-title">Depósito inicial</span>
                </template>
                <template #top-right>
                  <span>
                    {{ instrument.minimumInitialInvestment.formatted }}
                  </span>
                </template>
              </Row>

              <Row
                data-testid="minimumFinancialMovement-row"
                :has-interaction="false"
                :has-line="true"
              >
                <template #top-left>
                  <span class="row-title">Movimentação mínima</span>
                </template>
                <template #top-right>
                  <span>
                    {{ instrument.minimumFinancialMovement.formatted }}
                  </span>
                </template>
              </Row>

              <Row
                data-testid="liquidity-row"
                :has-interaction="false"
                :has-line="true"
              >
                <template #top-left>
                  <span class="row-title">Resgate em até</span>
                </template>
                <template #top-right>
                  <span>
                    {{ instrument.liquidity.formatted }}
                  </span>
                </template>
              </Row>

              <Row
                v-if="instrument.administrationTax && instrument.isFund"
                data-testid="administrationTax-row"
                :has-interaction="false"
                :has-line="true"
              >
                <template #top-left>
                  <span class="row-title">Taxa de Administração do fundo</span>
                </template>
                <template #top-right>
                  <span>
                    {{ instrument.administrationTax.formatted }}
                  </span>
                </template>
              </Row>

              <Row
                v-if="instrument.performanceTax && instrument.isFund"
                data-testid="performanceTax-row"
                :has-interaction="false"
                :has-line="true"
              >
                <template #top-left>
                  <span class="row-title">Taxa de Performance do fundo</span>
                </template>
                <template #top-right>
                  <span>
                    {{ instrument.performanceTax }}
                  </span>
                </template>
              </Row>

              <Row
                v-if="instrument.administrationTaxRebate"
                data-testid="cashbackTax-row"
                :has-interaction="false"
                :has-line="true"
              >
                <template #top-left>
                  <span class="row-title">Rebate de Administração</span>
                </template>
                <template #top-right>
                  <span>
                    {{ instrument.administrationTaxRebate }}
                  </span>
                </template>
              </Row>

              <Row
                v-if="instrument.performanceTaxRebate"
                data-testid="performanceCashbackTax-row"
                :has-interaction="false"
                :has-line="true"
              >
                <template #top-left>
                  <span class="row-title">Rebate de Performance</span>
                </template>
                <template #top-right>
                  <span>
                    {{ instrument.performanceTaxRebate }}
                  </span>
                </template>
              </Row>

              <Row
                data-testid="riskGrade-row"
                :has-interaction="false"
                :has-line="true"
              >
                <template #top-left>
                  <span class="row-title">Nível de risco</span>
                </template>
                <template #top-right>
                  <span class="align-tag-risk">
                    <TagRiskLevel
                      data-testid="tag-risk-level"
                      :risk-grade="instrument.riskGrade"
                    />
                  </span>
                </template>
              </Row>

              <Row
                v-if="showTypeInvestor"
                data-testid="investorType-row"
                :has-interaction="false"
                :has-line="true"
              >
                <template #top-left>
                  <span class="row-title">Publico alvo</span>
                </template>
                <template #top-right>
                  <span>
                    {{ investorTypeFormatted[instrument.investorType] }}
                  </span>
                </template>
              </Row>
            </div>

            <div data-testid="about-instrument" class="mb-xm">
              <Heading v-if="instrument.description" :level="3">Sobre</Heading>
              <p class="sobre-text" v-html="instrument.description" />
            </div>

            <div v-if="instrument.documents.length > 0">
              <Heading :level="3"> Documentos </Heading>
              <DownloadDocument
                :list="instrument.documents"
                @track-download-button="trackDownloadButton"
              />
            </div>
          </div>
        </div>
      </template>

      <template
        v-if="addProduct && !isLoading && instrument && showFooter"
        #footer
      >
        <ModalFooterWallet
          :left-button-label="leftButtonLabel"
          :right-button-label="rightButtonLabel"
          :show-right-button="isInstrumentAdded"
          :show-left-button="!isInstrumentAdded"
          :show-loading="productToLoading === instrument?.apiId"
          @left-button-clicked="leftButtonClicked"
          @right-button-clicked="rightButtonClicked"
        />
      </template>
    </BaseModal>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import {
  NavBar,
  Heading,
  BaseModal,
  Row,
  RoundedIcon,
  LoaderIndicator
} from '@warrenbrasil/nebraska-web';

import { ModalFooterWallet } from '@/modules/wallet-builder/src/components/Modals/ModalFooter';
import DownloadDocument from '../../DownloadDocument/DownloadDocument.vue';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

import TagRiskLevel from '../../TagRiskLevel/TagRiskLevel.vue';

import { BffInvestments } from '@/modules/wallet-builder/src/services/bff-investments';
import {
  IInstrumentDetails,
  IInstrumentDetailsFormatted
} from '../../../services/types/models/IBffInvestments';
import { getInstrumentDetails } from '@/modules/wallet-builder/src/services/bff-platorm';

@Component({
  components: {
    Row,
    LoaderIndicator,
    DownloadDocument,
    NavBar,
    BaseModal,
    ModalFooterWallet,
    Heading,
    TagRiskLevel,
    RoundedIcon
  }
})
export default class InstrumentDetails extends Vue {
  @Prop({ type: Boolean, default: false })
  public isOpen!: boolean;

  @Prop({ type: String, default: 'FECHAR' })
  public leftButtonLabel!: string;

  @Prop({ type: String, default: 'FECHAR' })
  public rightButtonLabel!: string;

  @Prop({ type: String })
  public instrumentDetailApiId?: string;

  @Prop({ type: Boolean, required: false, default: true })
  public addProduct?: boolean;

  @Prop({ type: Boolean, required: false, default: false })
  public isInstrumentAdded?: boolean;

  @Prop({ type: String, required: false, default: '' })
  public productToLoading!: string;

  @Prop({ type: Boolean, required: false, default: true })
  public showFooter!: boolean;

  private isLoading = false;

  public NebraskaColors = NebraskaColors;

  private instrument: IInstrumentDetails | null = null;
  private errors: Error | null = null;

  private showTypeInvestor = false;

  private investorTypeFormatted = {
    QUALIFIED: 'Investidor qualificado',
    PROFESSIONAL: 'Investidor profissional'
  };

  public bff = new BffInvestments();

  private get formattedInstrument(): IInstrumentDetailsFormatted {
    return {
      ...this.instrument,
      minimumFinancialMovement: this.instrument?.minimumFinancialMovement
        .value as number,
      minimumInitialInvestment: this.instrument?.minimumInitialInvestment
        .value as number
    };
  }

  private leftButtonClicked() {
    this.$emit('handle-instrument-add-clicked', this.formattedInstrument);
  }

  private rightButtonClicked() {
    this.$emit('handle-instrument-remove-clicked', this.formattedInstrument);
  }

  @Watch('instrumentDetailApiId')
  public async fetch() {
    this.isLoading = true;
    if (!this.instrumentDetailApiId) {
      return;
    }
    try {
      this.instrument = await getInstrumentDetails(this.instrumentDetailApiId);

      this.showTypeInvestor =
        this.instrument?.investorType === 'QUALIFIED' ||
        this.instrument?.investorType === 'PROFESSIONAL';

      this.trackLoadInstrumentDetails();
    } catch (e) {
      this.errors = e as Error;
    } finally {
      this.isLoading = false;
    }
  }

  trackDownloadButton(computedTitle: string) {
    this.$emit('track-download-button', computedTitle);
  }

  trackLoadInstrumentDetails() {
    if (this.isOpen && this.instrument) {
      this.$emit('track-load-instrument-details', this.instrument);
    }
  }

  private beforeMount() {
    if (this.instrumentDetailApiId) {
      this.fetch();
    }
  }
}
</script>

<style lang="less" scoped>
/deep/ .row-item > div .starts {
  margin-bottom: 4px !important;
}

.size-load {
  min-height: calc(100vh - 160px) !important;
}

.subtitle {
  font-size: 16px;
  font-weight: normal;
  color: @text-secondary;
}

.row-title {
  color: @text-secondary !important;
  @media screen and (min-width: 870px) {
    white-space: nowrap;
  }
}

.sobre-text {
  font-size: 20px;
}

.align-tag-risk div {
  display: flex;
  align-items: center;
  height: 100%;
}

h2 {
  margin: 0 0 @size-spacing-x150 0 !important;
}

h3 {
  margin: 0 0 8px 0 !important;
}

.my-xm {
  margin: @size-spacing-x600 0;
}

.mb-xm {
  margin-bottom: @size-spacing-x600;
}

.modal-content {
  padding: calc(@size-spacing-x800 - @size-spacing-x300)
    calc(@size-spacing-x1100 + @size-spacing-x300);
}

@media @mobile {
  .modal-content {
    padding: @size-spacing-x400;
  }
}
</style>
